import { isFedRamp } from '@atlassian/atl-context';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import {
	forgeProjectModuleResource,
	forgeServiceDeskQueueModuleResource,
} from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';

import { staticJSMProjectConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-jsm-connect-addons-queues/index.tsx';
import { staticSidebarJsmReportsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-jsm-reports-sidebar/index.tsx';
import { getCalendarFilterFieldsResource } from '@atlassian/jira-router-resources-servicedesk-calendar/src/services/filters/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectReportsSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-reports-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { kbConfluenceContextResource } from '@atlassian/jira-router-resources-confluence-context/src/services/index.tsx';

import { getConsolidationStateResource } from '@atlassian/jira-router-resources-eoc-tenant-info/src/services/index.tsx';
import {
	getNavigationResources,
	getNavigationSidebarProjectSettingsResource,
} from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import {
	projectContextResource,
	projectContextResourceWithCache,
} from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { serviceDeskAnalyticsContextResource } from '@atlassian/jira-router-resources-service-desk-analytics-context/src/services/index.tsx';
import { serviceDeskContextResource } from '@atlassian/jira-router-resources-service-desk-context/src/services/index.tsx';
import { servicedeskIsItsmProjectResource } from '@atlassian/jira-router-resources-service-desk-is-itsm-project/src/controllers/index.tsx';
import { servicedeskPracticesResource } from '@atlassian/jira-router-resources-service-desk-practices/src/controllers/index.tsx';
import {
	requiredFrontendProperties,
	requiredProjectProperties,
	firstProjectResource,
} from '@atlassian/jira-router-resources-service-desk-project-getting-started/src/services/index.tsx';
import {
	issueListSsrResource,
	priorityGroupByProjectIdResource,
	priorityGroupResource,
	queuesNavigationItems,
} from '@atlassian/jira-router-resources-service-desk-queues/src/services/index.tsx';
import { virtualAgentOnboardingResource } from '@atlassian/jira-router-resources-service-desk-virtual-agent/src/onboarding/index.tsx';
import { featuresResource } from '@atlassian/jira-router-resources-service-project-features/src/services/index.tsx';
import { incidentExperimentTraitResource } from '@atlassian/jira-router-resources-software-incidents/src/services/index.tsx';
import { resourceWithCondition2 } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import { workspaceResource } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/resources/index.tsx';
// getWillShowNav4 already loads it. Short-living experiment, to test performance improvement.
// eslint-disable-next-line jira/import-whitelist
import { expVal } from '@atlassian/jira-feature-experiments';
import { getBoardDetailsResource } from '@atlassian/jira-router-resources-classic-projects/src/services/board-details/index.tsx';
import {
	getRapidBoardDataResource,
	getRapidboardResources,
} from '@atlassian/jira-router-resources-classic-projects/src/services/main.tsx';
import { uifBoardResource } from '@atlassian/jira-router-resources-uif-board/src/index.tsx';
import { getEditModelResource } from '@atlassian/jira-router-resources-classic-projects/src/services/edit-model/index.tsx';

export const getServiceDeskNavigationResources = () => [
	...getNavigationResources(),
	// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
	incidentExperimentTraitResource,
	projectContextResource,
	forgeProjectModuleResource,
	staticSidebarShortcutsResource,
	staticProjectSidebarNavigationResource,
	staticProjectSidebarConnectAddonsNavigationResource,
	// end of resources expanded from getNavigationSidebarProjectResource
	...getServiceDeskNavigationSingleResources(),
];
const fetchPriorityGroupByProjectId = () =>
	fg('jsm_flash_priority_group_api_optimisation') && !getWillShowNav4();

export const getServiceDeskNavigationSingleResources = () => [
	resourceWithCondition2(() => fetchPriorityGroupByProjectId(), projectContextResource),
	...getServiceDeskExclusiveNavigationSingleResources(),
	...getServiceDeskSharedNavigationSingleResources(),
];

const isNav4ResourcesFixEnabled = () =>
	getWillShowNav4() && expVal('hot-113350-ssr-jsm-project-menu-fix', 'isEnabled', false);

/**
 * These resources only work correctly for Service Desk projects.
 * They will throw errors if used on non-Service Desk projects.
 */
export const getServiceDeskExclusiveNavigationSingleResources = () => [
	resourceWithCondition2(() => !fetchPriorityGroupByProjectId(), priorityGroupResource),
	resourceWithCondition2(() => fetchPriorityGroupByProjectId(), priorityGroupByProjectIdResource),
	featuresResource,
	staticSidebarJsmReportsResource,
	resourceWithCondition2(isNav4ResourcesFixEnabled, getConsolidationStateResource()),
	resourceWithCondition2(isNav4ResourcesFixEnabled, servicedeskIsItsmProjectResource),
];

/**
 * These resources are able to handle both Service Desk and non-Service Desk projects.
 */
export const getServiceDeskSharedNavigationSingleResources = () => [
	queuesNavigationItems,
	resourceWithCondition2(() => fg('ssr_nav4_queues_sidebar'), requiredProjectProperties),
	resourceWithCondition2(() => fg('ssr_nav4_queues_sidebar'), requiredFrontendProperties),
	forgeServiceDeskQueueModuleResource,
	staticJSMProjectConnectAddonsNavigationResource,
	staticProjectReportsSidebarConnectAddonsNavigationResource,
	// for Getting Started menu item
	// resourceWithCondition2(isNav4ResourcesFixEnabled, requiredFrontendProperties),
	resourceWithCondition2(isNav4ResourcesFixEnabled, projectContextResourceWithCache),
	resourceWithCondition2(isNav4ResourcesFixEnabled, firstProjectResource),
];

export const getServiceDeskSettingsResources = () => [
	...getNavigationResources(),
	// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
	incidentExperimentTraitResource,
	projectContextResource,
	forgeProjectModuleResource,
	staticSidebarShortcutsResource,
	staticProjectSidebarNavigationResource,
	staticProjectSidebarConnectAddonsNavigationResource,
	// end of resources expanded from getNavigationSidebarProjectResource
	...getNavigationSidebarProjectSettingsResource(),
	servicedeskPracticesResource,
	serviceDeskContextResource,
	serviceDeskAnalyticsContextResource,
	resourceWithCondition2(() => !isFedRamp(), virtualAgentOnboardingResource),
];

export const getServicedeskPageContextResources = () => [
	projectContextResourceWithCache,
	serviceDeskContextResource,
	serviceDeskAnalyticsContextResource,
];

export const getQueuesResources = () => [
	...getServicedeskPageContextResources(),
	...getServiceDeskNavigationResources(),
	resourceWithCondition2(
		() => ff('automation-templates-team-types-refactor-m1_isj0e'),
		servicedeskPracticesResource,
	),
	resourceWithCondition2(
		() => fg('corcap-2944-add-jqlbuilder-to-jsm-queues'),
		getCalendarFilterFieldsResource(),
	),
];

export const getIssueListSsrResource = () => [
	resourceWithCondition2(() => fg('ssr_view_queues_issuelist'), issueListSsrResource),
];

export const getKnowledgeBaseResources = () => [kbConfluenceContextResource];

export const getInsightResources = () => [workspaceResource];

export const getQueueConfigResource = () => [
	resourceWithCondition2(
		() => !fg('jsm-views-inside-queues-fastfollow') && fg('jsm_views_inside_queues_-_main_flag'),
		priorityGroupResource,
	),
	resourceWithCondition2(() => fg('jsm_views_inside_queues_-_main_flag'), projectContextResource),
];

export const getQueueViewConfigResource = () => [
	resourceWithCondition2(
		() => !fg('jsm-views-inside-queues-fastfollow') && fg('jsm_views_inside_queues_-_main_flag'),
		getRapidBoardDataResource(),
	),
	resourceWithCondition2(() => fg('jsm_views_inside_queues_-_main_flag'), getEditModelResource()),
	resourceWithCondition2(
		() => fg('jsm_views_inside_queues_-_main_flag'),
		getBoardDetailsResource(),
	),
	resourceWithCondition2(
		() => !fg('jsm-views-inside-queues-fastfollow') && fg('jsm_views_inside_queues_-_main_flag'),
		priorityGroupResource,
	),
	resourceWithCondition2(() => fg('jsm_views_inside_queues_-_main_flag'), projectContextResource),
	resourceWithCondition2(() => fg('jsm_views_inside_queues_-_main_flag'), uifBoardResource),
	...getRapidboardResources().map((resource) =>
		resourceWithCondition2(() => fg('jsm_views_inside_queues_-_main_flag'), resource),
	),
];
